import {
  Mail,
  MessageSquareText,
  Phone,
  CreditCard,
  Globe,
  Lock,
  AlertCircle,
  ShieldCheck,
  KeyRound,
  UserX,
  BookOpen,
  Clock,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import SEO from "@/components/SEO";
import ShareSection from "@/components/ShareSection";
import { articles } from "@/data/articles";

const truffe = [
  {
    icon: MessageSquareText,
    title: "Il finto nipote su WhatsApp",
    color: "bg-red-50 text-red-700 border-red-200",
    desc:
      "Ti scrivono da un numero sconosciuto dicendo 'Ciao nonna/nonno, ho perso il telefono'. Ti chiedono di mandare soldi su un IBAN o ricariche. È QUASI SEMPRE UNA TRUFFA.",
    signals: [
      "Numero sconosciuto",
      "Richiesta urgente di soldi",
      "Non vogliono parlare al telefono",
    ],
    actions: [
      "Chiama subito il tuo vero nipote al suo numero solito",
      "Non mandare soldi né dati",
      "Blocca il numero su WhatsApp",
    ],
  },
  {
    icon: Mail,
    title: "Email falsa dalla banca o dalle Poste",
    color: "bg-yellow-50 text-yellow-700 border-yellow-200",
    desc:
      "Ricevi un'email che sembra della tua banca o di Poste Italiane: 'Il tuo conto è bloccato, clicca qui per sbloccarlo'. È phishing.",
    signals: [
      "Link che chiede di inserire password o codici",
      "Errori di italiano o frasi strane",
      "Tono allarmistico o di urgenza",
    ],
    actions: [
      "Non cliccare sul link",
      "Chiama la tua banca usando il numero stampato sulla tessera",
      "Cancella l'email",
    ],
  },
  {
    icon: Phone,
    title: "Falso operatore (banca, INPS, energia elettrica)",
    color: "bg-orange-50 text-orange-700 border-orange-200",
    desc:
      "Ti chiamano dicendo di essere della banca, dell'INPS o del fornitore di luce/gas. Vogliono codici, password o ti propongono offerte 'imperdibili'.",
    signals: [
      "Chiede codici, password o dati personali",
      "Vuole farti firmare o cambiare contratto al telefono",
      "Numero che non riconosci",
    ],
    actions: [
      "Chiudi la chiamata",
      "Richiama tu il numero ufficiale dell'ente",
      "Non dare MAI codici della banca al telefono",
    ],
  },
  {
    icon: CreditCard,
    title: "SMS con pacco da ritirare o multa da pagare",
    color: "bg-red-50 text-red-700 border-red-200",
    desc:
      "SMS tipo 'Il tuo pacco è in giacenza, paga 2€ per consegnarlo' con un link. Quei 2€ aprono la porta a furti molto più grandi.",
    signals: [
      "Link strano (non finisce con .it, .com noti)",
      "Richiesta di pagamento per somme piccole",
      "Mittente sconosciuto",
    ],
    actions: [
      "Non cliccare sul link",
      "Verifica il pacco sull'app ufficiale del corriere",
      "Cancella l'SMS",
    ],
  },
  {
    icon: Globe,
    title: "Sito di acquisti troppo conveniente",
    color: "bg-yellow-50 text-yellow-700 border-yellow-200",
    desc:
      "Un sito che vende televisori, telefoni o medicine a metà prezzo. Spesso non arriva nulla, oppure rubano i dati della carta.",
    signals: [
      "Prezzi troppo bassi rispetto ai negozi",
      "Indirizzo del sito strano",
      "Non c'è un contatto telefonico chiaro",
    ],
    actions: [
      "Compra solo da siti che conosci",
      "Chiedi a un familiare prima di comprare",
      "Usa carta prepagata con poco saldo",
    ],
  },
  {
    icon: AlertCircle,
    title: "Vincita o eredità inaspettata",
    color: "bg-red-50 text-red-700 border-red-200",
    desc:
      "Ti dicono 'Hai vinto un premio' o 'C'è un'eredità per te', ma devi pagare prima 'le spese'. Non c'è nessun premio.",
    signals: [
      "Non hai partecipato a nessun concorso",
      "Devi pagare per ricevere il premio",
      "Vogliono i tuoi dati bancari",
    ],
    actions: [
      "Non rispondere",
      "Non pagare nulla",
      "Avvisa un familiare",
    ],
  },
];

const regole = [
  {
    icon: Lock,
    title: "Mai dare codici o password",
    desc: "Nessuna banca, ente o azienda seria ti chiederà mai password, PIN o codici via telefono, SMS o email.",
  },
  {
    icon: KeyRound,
    title: "Usa password lunghe e diverse",
    desc: "Per ogni servizio (email, banca, Facebook) usa una password diversa. Scrivile su un quadernetto al sicuro in casa.",
  },
  {
    icon: ShieldCheck,
    title: "Attiva la doppia sicurezza",
    desc: "Chiedi a un familiare di attivare la 'verifica in due passaggi' su email e banca. È una protezione fortissima.",
  },
  {
    icon: UserX,
    title: "Nel dubbio, non rispondere",
    desc: "Se un messaggio ti fa sentire confuso, di fretta o spaventato, è un campanello d'allarme. Fermati e chiedi aiuto.",
  },
];

export default function Guide() {
  return (
    <div data-testid="guide-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <SEO
        path="/guida"
        title="Guida Anti-Truffe: SMS truffa, WhatsApp truffa, phishing"
        description="Le truffe più diffuse in Italia spiegate semplici: SMS truffa del pacco, WhatsApp del finto nipote, phishing di banca e Poste, email sospette. Segnali di allarme e cosa fare."
      />
      <div className="text-center mb-14 animate-fade-up">
        <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#22C55E]/15 text-[#16A34A] text-base font-bold mb-5">
          <ShieldCheck size={20} /> Guida Anti-Truffe
        </span>
        <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-[#1E3A8A] leading-tight">
          Conoscere le truffe è il primo passo per evitarle
        </h1>
        <p className="mt-5 text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
          Qui trovi le truffe più comuni in Italia, con i segnali di allarme
          e cosa fare. Leggile con calma — sono scritte semplici.
        </p>
      </div>

      <div className="bg-white rounded-[2rem] shadow-lg border-4 border-blue-50 p-6 md:p-10 mb-12">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1E3A8A] mb-6">
          Le truffe più diffuse — clicca per leggere
        </h2>
        <Accordion type="single" collapsible className="space-y-3">
          {truffe.map((t, i) => (
            <AccordionItem
              key={t.title}
              value={`item-${i}`}
              data-testid={`truffa-item-${i}`}
              className="border-2 border-gray-200 rounded-2xl px-5 data-[state=open]:border-[#1E3A8A] data-[state=open]:bg-blue-50/30"
            >
              <AccordionTrigger className="text-lg md:text-xl font-bold text-[#1E3A8A] hover:no-underline py-5">
                <div className="flex items-center gap-4 text-left">
                  <span className={`w-12 h-12 rounded-xl border-2 flex items-center justify-center ${t.color}`}>
                    <t.icon size={24} />
                  </span>
                  {t.title}
                </div>
              </AccordionTrigger>
              <AccordionContent className="text-lg text-gray-700 leading-relaxed pb-6">
                <p className="mb-5">{t.desc}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5">
                    <div className="font-bold text-red-800 mb-2">
                      Campanelli d'allarme
                    </div>
                    <ul className="space-y-2">
                      {t.signals.map((s) => (
                        <li key={s} className="flex items-start gap-2">
                          <span className="text-red-600 font-bold">!</span>
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5">
                    <div className="font-bold text-green-800 mb-2">Cosa fare</div>
                    <ul className="space-y-2">
                      {t.actions.map((a) => (
                        <li key={a} className="flex items-start gap-2">
                          <span className="text-green-700 font-bold">✓</span>
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#1E3A8A] mb-8 text-center">
        Le 4 regole d'oro della sicurezza online
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {regole.map((r, i) => (
          <div
            key={r.title}
            data-testid={`regola-${i}`}
            className="bg-white p-7 rounded-3xl shadow-lg border-2 border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all"
          >
            <div className="w-16 h-16 rounded-2xl bg-[#DBEAFE] flex items-center justify-center text-[#1E3A8A] mb-5">
              <r.icon size={32} />
            </div>
            <h3 className="font-heading text-xl md:text-2xl font-bold text-[#1E3A8A] mb-3">
              {r.title}
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed">{r.desc}</p>
          </div>
        ))}
      </div>

      <section className="mt-16" data-testid="guide-antitruffa-section">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#DBEAFE] text-[#1E3A8A] text-base font-bold mb-5">
            <BookOpen size={20} /> Guide antitruffa
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-[#1E3A8A]">
            Approfondimenti sulle truffe più diffuse
          </h2>
          <p className="mt-4 text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Guide dettagliate, scritte in italiano semplice, per riconoscere e
            difenderti dalle truffe che circolano di più.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={`/guida/${a.slug}`}
              data-testid={`guida-articolo-${a.slug}`}
              className="flex flex-col bg-white p-7 rounded-3xl shadow-lg border-2 border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all"
            >
              <span className="inline-flex items-center gap-2 text-sm font-bold text-[#16A34A] mb-3">
                <BookOpen size={16} /> {a.category}
              </span>
              <h3 className="font-heading text-xl font-bold text-[#1E3A8A] leading-snug mb-3">
                {a.title}
              </h3>
              <p className="text-base text-gray-600 leading-relaxed mb-4 flex-1">
                {a.description}
              </p>
              <span className="inline-flex items-center gap-2 text-base font-bold text-[#1E3A8A]">
                Leggi la guida →
                <span className="inline-flex items-center gap-1 text-sm text-gray-500 font-semibold ml-auto">
                  <Clock size={15} /> {a.readMinutes} min
                </span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      <div className="mt-14">
        <ShareSection variant="compact" />
      </div>
    </div>
  );
}
